import { useState, useEffect } from 'react';
import api from './api';
import './AccountManagement.css';

const STATUS_LABELS = { 0: '대기', 1: '운영중' };
const fmtDate = v => v ? v.replace('T', ' ') : '-';
const EMPTY_CREATE = {
  vertiportId: '', name: '', lat: '', lng: '',
  status: 1, description: '', runways: 0, capacity: 0,
};

export default function VertiportManagement() {
  const [vertiports, setVertiports]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selected, setSelected]           = useState(null);
  const [mode, setMode]                   = useState('view'); // 'view' | 'create'

  const [editForm, setEditForm]     = useState({ name: '', lat: '', lng: '', status: 1, description: '', runways: 0, capacity: 0 });
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);

  const [saving, setSaving]               = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [msg, setMsg]                     = useState({ text: '', type: '' });

  useEffect(() => { fetchList(); }, []);

  async function fetchList() {
    try {
      const res = await api.get('/api/vertiport/list');
      setVertiports(res.data);
    } catch {
      setMsg({ text: '버티포트 목록을 불러오지 못했습니다.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function selectVertiport(vtp) {
    setSelected(vtp);
    setEditForm({
      name: vtp.name, lat: vtp.lat, lng: vtp.lng,
      status: vtp.status, description: vtp.description ?? '',
      runways: vtp.runways, capacity: vtp.capacity,
    });
    setMode('view');
    setMsg({ text: '', type: '' });
    setConfirmDelete(false);
  }

  function openCreate() {
    setSelected(null);
    setCreateForm(EMPTY_CREATE);
    setMode('create');
    setMsg({ text: '', type: '' });
    setConfirmDelete(false);
  }

  function setEdit(key, val)   { setEditForm(f => ({ ...f, [key]: val })); }
  function setCreate(key, val) { setCreateForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const body = {
        name:        editForm.name,
        lat:         parseFloat(editForm.lat),
        lng:         parseFloat(editForm.lng),
        status:      Number(editForm.status),
        description: editForm.description || null,
        runways:     Number(editForm.runways),
        capacity:    Number(editForm.capacity),
      };
      const res = await api.put(`/api/vertiport/${selected.vertiportUid}`, body);
      if (res.data.success !== false) {
        setMsg({ text: '수정되었습니다.', type: 'success' });
        const updated = { ...selected, ...body };
        setVertiports(prev => prev.map(v => v.vertiportUid === selected.vertiportUid ? updated : v));
        setSelected(updated);
      } else {
        setMsg({ text: res.data.message || '수정에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleCreate() {
    const { vertiportId, name, lat, lng } = createForm;
    if (!vertiportId.trim() || !name.trim() || lat === '' || lng === '') {
      setMsg({ text: 'ID, 이름, 위도, 경도는 필수입니다.', type: 'error' });
      return;
    }
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const res = await api.post('/api/vertiport/create', {
        vertiportId:  vertiportId.trim(),
        name:         name.trim(),
        lat:          parseFloat(lat),
        lng:          parseFloat(lng),
        status:       Number(createForm.status),
        description:  createForm.description || null,
        runways:      Number(createForm.runways),
        capacity:     Number(createForm.capacity),
      });
      if (res.data.success) {
        setMsg({ text: '버티포트가 등록되었습니다.', type: 'success' });
        await fetchList();
        setMode('view');
        setCreateForm(EMPTY_CREATE);
      } else {
        setMsg({ text: res.data.message || '등록에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selected) return;
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const res = await api.delete(`/api/vertiport/${selected.vertiportUid}`);
      if (res.data.success !== false) {
        setVertiports(prev => prev.filter(v => v.vertiportUid !== selected.vertiportUid));
        setSelected(null);
        setMode('view');
        setConfirmDelete(false);
      } else {
        setMsg({ text: res.data.message || '삭제에 실패했습니다.', type: 'error' });
        setConfirmDelete(false);
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  }

  const formFields = (form, setFn, isCreate) => (
    <>
      {isCreate && (
        <div className="acm-row">
          <label>버티포트 ID *</label>
          <input type="text" value={form.vertiportId}
            onChange={e => setFn('vertiportId', e.target.value)}
            className="acm-input" autoFocus maxLength={10} placeholder="예: VTP-A" />
        </div>
      )}
      <div className="acm-row">
        <label>이름 *</label>
        <input type="text" value={form.name}
          onChange={e => setFn('name', e.target.value)}
          className="acm-input" />
      </div>
      <div className="acm-row">
        <label>위도 *</label>
        <input type="number" value={form.lat}
          onChange={e => setFn('lat', e.target.value)}
          className="acm-input" step="0.000001" placeholder="예: 35.892000" />
      </div>
      <div className="acm-row">
        <label>경도 *</label>
        <input type="number" value={form.lng}
          onChange={e => setFn('lng', e.target.value)}
          className="acm-input" step="0.000001" placeholder="예: 128.675000" />
      </div>
      <div className="acm-row">
        <label>상태</label>
        <select value={form.status} onChange={e => setFn('status', e.target.value)} className="acm-input">
          <option value={1}>운영중</option>
          <option value={0}>대기</option>
        </select>
      </div>
      <div className="acm-row">
        <label>활주로 수</label>
        <input type="number" value={form.runways}
          onChange={e => setFn('runways', e.target.value)}
          className="acm-input" min={0} />
      </div>
      <div className="acm-row">
        <label>수용 용량</label>
        <input type="number" value={form.capacity}
          onChange={e => setFn('capacity', e.target.value)}
          className="acm-input" min={0} />
      </div>
      <div className="acm-row acm-row-top">
        <label>설명</label>
        <textarea value={form.description}
          onChange={e => setFn('description', e.target.value)}
          className="acm-input acm-textarea" rows={3} placeholder="선택 입력" />
      </div>
    </>
  );

  return (
    <div className="acm">
      {/* ── 목록 ── */}
      <div className="acm-list">
        <div className="acm-list-hd">
          <span>버티포트 목록 {!loading && `(${vertiports.length})`}</span>
          <button className="acm-create-btn" onClick={openCreate}>+ 버티포트 등록</button>
        </div>
        {loading ? (
          <div className="acm-placeholder">불러오는 중...</div>
        ) : vertiports.length === 0 ? (
          <div className="acm-placeholder">등록된 버티포트가 없습니다.</div>
        ) : (
          <ul className="acm-items">
            {vertiports.map(vtp => (
              <li key={vtp.vertiportUid}>
                <button
                  className={`acm-item${selected?.vertiportUid === vtp.vertiportUid && mode === 'view' ? ' active' : ''}`}
                  onClick={() => selectVertiport(vtp)}
                >
                  <div className="acm-item-top">
                    <span className="acm-item-name">{vtp.name}</span>
                    <span className={`acm-badge vtp-status-${vtp.status}`}>
                      {STATUS_LABELS[vtp.status] ?? vtp.status}
                    </span>
                  </div>
                  <div className="acm-item-id">{vtp.vertiportId}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── 상세 / 수정 / 생성 ── */}
      <div className="acm-detail">
        {mode === 'create' ? (
          <div className="acm-form">
            <div className="acm-form-hd">버티포트 등록</div>
            <div className="acm-section-label">위치 정보</div>
            {formFields(createForm, setCreate, true)}
            {msg.text && <p className={`acm-msg acm-msg-${msg.type}`}>{msg.text}</p>}
            <div className="acm-btn-row">
              <button className="acm-save-btn" onClick={handleCreate} disabled={saving}>
                {saving ? '등록 중...' : '등록'}
              </button>
              <button className="acm-cancel-btn" onClick={() => { setMode('view'); setMsg({ text: '', type: '' }); }}>
                취소
              </button>
            </div>
          </div>
        ) : !selected ? (
          <div className="acm-placeholder">좌측 목록에서 버티포트를 선택하세요.</div>
        ) : (
          <div className="acm-form">
            <div className="acm-form-hd">{selected.name}</div>

            <div className="acm-section-label">기본 정보</div>
            <div className="acm-row">
              <label>버티포트 ID</label>
              <input type="text" value={selected.vertiportId} readOnly className="acm-input readonly" />
            </div>
            <div className="acm-row">
              <label>등록일</label>
              <input type="text" value={fmtDate(selected.regDate)} readOnly className="acm-input readonly" />
            </div>

            <div className="acm-section-label">수정</div>
            {formFields(editForm, setEdit, false)}

            {msg.text && <p className={`acm-msg acm-msg-${msg.type}`}>{msg.text}</p>}

            <div className="acm-btn-row">
              <button className="acm-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '수정'}
              </button>
              {confirmDelete ? (
                <>
                  <span className="acm-del-confirm">정말 삭제하시겠습니까?</span>
                  <button className="acm-save-btn acm-del-ok" onClick={handleDelete} disabled={saving}>확인</button>
                  <button className="acm-cancel-btn" onClick={() => setConfirmDelete(false)}>취소</button>
                </>
              ) : (
                <button className="acm-cancel-btn acm-del-btn" onClick={() => setConfirmDelete(true)}>삭제</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
